import React, { useState, useRef, useCallback, useEffect } from 'react';

export const ImageComparison = ({ beforeImage, afterImage, altBefore = 'Before', altAfter = 'After' }) => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef(null);

    const handleMove = useCallback((clientX) => {
        if (!isDragging || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        let newPosition = ((clientX - rect.left) / rect.width) * 100;
        newPosition = Math.max(0, Math.min(100, newPosition));
        setSliderPosition(newPosition);
    }, [isDragging]);

    const handleMouseDown = () => setIsDragging(true);
    const handleMouseUp = () => setIsDragging(false);
    const handleMouseMove = (e) => handleMove(e.clientX);
    const handleTouchStart = () => setIsDragging(true);
    const handleTouchEnd = () => setIsDragging(false);
    const handleTouchMove = (e) => handleMove(e.touches[0].clientX);

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                position: 'relative',
                width: '100%',
                userSelect: 'none',
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                cursor: isDragging ? 'ew-resize' : 'default',
            }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {/* Before image — bottom layer */}
            <img
                src={beforeImage}
                alt={altBefore}
                draggable="false"
                style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
            />

            {/* After image — clipped top layer */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '100%',
                    overflow: 'hidden',
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                }}
            >
                <img
                    src={afterImage}
                    alt={altAfter}
                    draggable="false"
                    style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left' }}
                />
            </div>

            {/* Labels */}
            <span style={{
                position: 'absolute', top: 14, left: 16,
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
                padding: '4px 10px', borderRadius: 20, pointerEvents: 'none',
            }}>BEFORE</span>
            <span style={{
                position: 'absolute', top: 14, right: 16,
                background: 'rgba(0,0,0,0.55)', color: '#fff',
                fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
                padding: '4px 10px', borderRadius: 20, pointerEvents: 'none',
            }}>AFTER</span>

            {/* Slider handle */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: 'rgba(255,255,255,0.9)',
                    left: `calc(${sliderPosition}% - 1.5px)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'ew-resize',
                }}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
            >
                <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: '#fff',
                    boxShadow: isDragging ? '0 4px 24px rgba(0,0,0,0.22)' : '0 2px 12px rgba(0,0,0,0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transform: isDragging ? 'scale(1.1)' : 'scale(1)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    flexShrink: 0,
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
                        fill="none" stroke="#555" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                        <polyline points="9 18 3 12 9 6" />
                    </svg>
                </div>
            </div>
        </div>
    );
};
